import { ComponentPropsWithoutRef, Fragment, ReactNode } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { useText } from '../../utils/hooks/use_text';
import { Pagination, type PaginationProps } from '../pagination';
import { VisuallyHidden } from '../visually_hidden';
import { TableBody } from './_table_body.component';
import { TableHeaderCell } from './_table_header_cell.component';
import { theme } from './table.theme';

export type TableAlign = 'left' | 'center' | 'right';
export type TableSortDirection = 'asc' | 'desc';

export interface TableSort {
	columnKey: string;
	direction: TableSortDirection;
}

interface TableColumnBase<TRow> {
	/**
	 * Unique identifier for the column. Used as the React key for the header
	 * cell and reported back via `onSortChange`.
	 */
	key: string;

	/**
	 * CSS width applied to the column, e.g. `'180px'`, `'20%'`.
	 */
	width?: string;

	/**
	 * CSS min-width of the cell content, e.g. `'80px'`. Use an absolute
	 * length; percentages don't resolve in auto table layout.
	 */
	minWidth?: string;

	/**
	 * CSS max-width of the cell content, e.g. `'240px'`. Stops long content
	 * from growing the column. Use an absolute length; percentages don't
	 * resolve in auto table layout.
	 */
	maxWidth?: string;

	/**
	 * Text alignment for both the header and the cells in this column.
	 * @default 'left'
	 */
	align?: TableAlign;

	/**
	 * Whether this column can be sorted. When `true`, the header renders as a
	 * clickable, keyboard-accessible control that calls `onSortChange`.
	 * @default false
	 */
	sortable?: boolean;

	/**
	 * Whether this column can be hidden from the column visibility/reorder
	 * menu (see `useTableColumnPreferences`/`TableColumnMenu`). When `false`,
	 * the column can't be toggled off, and is always shown regardless of
	 * `defaultHiddenColumns`.
	 * @default true
	 */
	canHide?: boolean;

	/**
	 * Renders the content of a cell for this column, given the row's data.
	 */
	render: (row: TRow) => ReactNode;
}

/**
 * `label` is only optional when `header` is already a plain string - a
 * non-string `header` (e.g. an icon or a `<strong>`) has no text content the
 * column visibility/reorder menu or a sortable header's accessible name can
 * fall back to, so `label` is required in that case.
 */
type TableColumnTruncation<TRow> =
	| {
			truncate?: false;
			renderTooltip?: never;
	  }
	| {
			/**
			 * Keeps the cell content on one line and clips overflow with an
			 * ellipsis at `maxWidth`. When clipped, a tooltip shows the full
			 * content.
			 */
			truncate: true;
			maxWidth: string;

			/**
			 * Overrides the tooltip content shown when the cell content is
			 * clipped. Defaults to the rendered cell content.
			 */
			renderTooltip?: (row: TRow) => ReactNode;
	  };

type TableColumnContent<TRow> =
	| (TableColumnBase<TRow> & {
			/**
			 * Content rendered in the column header.
			 */
			header: string;

			/**
			 * Plain-text label for the column, used where a `ReactNode` can't be
			 * rendered directly - e.g. in a column visibility/reorder menu (see
			 * `useTableColumnPreferences`/`TableColumnMenu`). Falls back to
			 * `header` when it's already a plain string.
			 */
			label?: string;
	  })
	| (TableColumnBase<TRow> & {
			header: Exclude<ReactNode, string>;
			label: string;
	  });

export type TableColumn<TRow> = TableColumnContent<TRow> & TableColumnTruncation<TRow>;

export type TablePagination = PaginationProps;

interface TableBaseProps<TRow> {
	/**
	 * The columns to render, in order.
	 */
	columns: TableColumn<TRow>[];

	/**
	 * The rows to render. Only the rows provided are rendered - pagination is
	 * expected to be handled by the caller (e.g. the backend).
	 */
	rows: TRow[];

	/**
	 * When `true`, renders `skeletonRows` skeleton rows instead of `rows`.
	 * @default false
	 */
	loading?: boolean;

	/**
	 * Number of skeleton rows to render while `loading` is `true`.
	 * @default 10
	 */
	skeletonRows?: number;

	/**
	 * The currently active sort, if any. Purely presentational - sorting the
	 * data itself is the caller's responsibility.
	 */
	sort?: TableSort;

	/**
	 * Called when the user requests a different sort by clicking a `sortable`
	 * column header. Toggles between ascending and descending; clicking a new
	 * column always starts at ascending.
	 */
	onSortChange?: (columnKey: string, direction: TableSortDirection) => void;

	/**
	 * Wraps the table (and `header`/`pagination`, if provided) in an
	 * elevated, white, rounded card.
	 * @default true
	 */
	elevated?: boolean;

	/**
	 * Rendered above the table, inside the card. No padding is applied - the
	 * caller controls its own spacing (e.g. to place a full-width divider).
	 */
	header?: ReactNode;

	/**
	 * An accessible name for the table, e.g. `'Patients'`. Rendered as a
	 * visually-hidden `<caption>`, so it doesn't duplicate a visible title
	 * you already render via `header` - it exists purely so screen reader
	 * users landing on the table (e.g. via table-navigation mode) know what
	 * it represents. Provide this whenever the table's purpose isn't already
	 * obvious from a visible heading immediately before it.
	 */
	caption?: string;

	/**
	 * Content rendered in place of the rows when `rows` is empty and the
	 * table isn't `loading` - e.g. `'No patients found.'`. Defaults to a
	 * generic localized message. A silently empty table body gives no
	 * indication to a screen reader user of whether data failed to load, is
	 * still loading, or the result set is genuinely empty.
	 */
	emptyState?: ReactNode;

	/**
	 * Renders numbered page buttons plus Previous/Next controls below the
	 * table, inside the card. Automatically hidden when `pageCount <= 1`.
	 */
	pagination?: TablePagination;

	/**
	 * Returns a stable, unique key for a row, used as the React key. Required
	 * so React can correctly preserve row identity across re-renders (e.g.
	 * after a drag-and-drop reorder or a live sort/filter update) - otherwise
	 * state associated with a row (e.g. focus, animations) can end up
	 * attached to the wrong row after `rows` changes.
	 */
	getRowKey: (row: TRow) => string | number;
}

/**
 * A row is only clickable if it also has an accessible action name: making a
 * `<tr>` focusable and pressing Enter/Space on it doesn't announce anything
 * to assistive technology beyond "row", so `getRowActionLabel` is required
 * alongside `onRowClick` rather than being an afterthought.
 */
export type TableRowClickProps<TRow> =
	| {
			/**
			 * Called when a row is clicked, unless the click originated from an
			 * interactive element inside the row (e.g. a button or link).
			 */
			onRowClick: (row: TRow, isCtrlClick: boolean) => void;

			/**
			 * The accessible name for a row's action, e.g. `(row) => \`View ${row.name}\``.
			 * Rendered as a visually-hidden button at the end of the row so
			 * keyboard/screen-reader users get a real, labeled action rather than
			 * an unnamed focusable row.
			 */
			getRowActionLabel: (row: TRow) => string;
	  }
	| {
			onRowClick?: never;
			getRowActionLabel?: never;
	  };

export type TableProps<TRow> = TableBaseProps<TRow> & TableRowClickProps<TRow>;

const StyledTable = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const StyledCaption = styled.caption`
	border: 0;
	clip: rect(1px, 1px, 1px, 1px);
	clip-path: inset(50%);
	height: 1px;
	margin: -1px;
	overflow: hidden;
	padding: 0;
	position: absolute;
	width: 1px;
	word-wrap: normal !important;
`;

const StyledHeaderRow = styled.tr`
	border-bottom: 2px solid ${theme.getValue('headerBorderColor')};
`;

const StyledCardWrapper = styled.div`
	background: ${theme.getValue('cardBackground')};
	border-radius: ${coreTokens.borderRadius.medium};
	box-shadow: ${coreTokens.elevation.skim};
	overflow: hidden;
`;

const StyledScrollArea = styled.div`
	width: 100%;
	overflow-x: auto;

	&:focus-visible {
		outline: 2px solid ${theme.getValue('focusRingColor')};
		outline-offset: -2px;
	}
`;

const StyledHeaderSlot = styled.div`
	box-sizing: border-box;
	width: 100%;
	min-width: 0;
	overflow: hidden;
	border-bottom: 1px solid ${theme.getValue('rowBorderColor')};

	/*
	 * Cellula's Box/Flex default to box-sizing: content-box, so a header
	 * that combines width: 100% with its own padding (the expected pattern,
	 * since Table applies none) would render wider than this slot. Force
	 * border-box on the header's own root node so its padding is included
	 * in - not added to - its 100% width.
	 */
	& > * {
		box-sizing: border-box;
		max-width: 100%;
	}
`;

const StyledPaginationRow = styled.div`
	box-sizing: border-box;
	padding: ${coreTokens.spacing.x2} ${coreTokens.spacing.x0_5};
	background: ${theme.getValue('paginationBackground')};
`;

/**
 * A component for rendering tabular data from a set of `columns` and `rows`.
 */
export function Table<TRow>({
	columns,
	rows,
	loading = false,
	skeletonRows = 10,
	sort,
	onSortChange,
	onRowClick,
	getRowActionLabel,
	elevated = true,
	header,
	caption,
	emptyState,
	pagination,
	getRowKey,
	...rest
}: TableProps<TRow> & Omit<ComponentPropsWithoutRef<'table'>, 'children'>): JSX.Element {
	const staticTexts = useText();
	const scrollAreaLabel = caption
		? staticTexts('Component.Table.ScrollableRegion', { table: caption })
		: staticTexts('Component.Table.ScrollableRegionUnnamed');

	const content = (
		<Fragment>
			{header ? <StyledHeaderSlot>{header}</StyledHeaderSlot> : null}
			<VisuallyHidden role='status'>{loading ? staticTexts('Component.Table.Loading') : null}</VisuallyHidden>
			<StyledScrollArea role='region' tabIndex={0} aria-label={scrollAreaLabel}>
				<StyledTable {...rest} aria-busy={loading}>
					{caption ? <StyledCaption>{caption}</StyledCaption> : null}
					<colgroup>
						{columns.map((column) => (
							<col key={column.key} style={column.width ? { width: column.width } : undefined} />
						))}
					</colgroup>
					<thead>
						<StyledHeaderRow>
							{columns.map((column) => (
								<TableHeaderCell key={column.key} column={column} sort={sort} onSortChange={onSortChange} />
							))}
						</StyledHeaderRow>
					</thead>
					<tbody>
						<TableBody
							columns={columns}
							rows={rows}
							loading={loading}
							skeletonRows={skeletonRows}
							emptyState={emptyState}
							emptyStateText={staticTexts('Component.Table.EmptyState')}
							onRowClick={onRowClick}
							getRowActionLabel={getRowActionLabel}
							getRowKey={getRowKey}
						/>
					</tbody>
				</StyledTable>
			</StyledScrollArea>
			{pagination && pagination.pageCount > 1 ? (
				<StyledPaginationRow>
					<Pagination {...pagination} />
				</StyledPaginationRow>
			) : null}
		</Fragment>
	);

	if (!elevated) {
		return content;
	}

	return <StyledCardWrapper>{content}</StyledCardWrapper>;
}
